import React, { useRef, useState } from "react";
import { Accessibility, Building2, ChevronDown } from "lucide-react";
import AccessibilityPanel from "@/components/AccessibilityPanel";
import { useOrganisation } from "@/lib/OrganisationContext";
import ActAsDialog from "@/components/ActAsDialog";

export default function TopBar({ title }) {
  const [showA11y, setShowA11y] = useState(false);
  const [showOrgMenu, setShowOrgMenu] = useState(false);
  const [actAsTarget, setActAsTarget] = useState(null);
  const [actAsBusy, setActAsBusy] = useState(false);
  const [actAsError, setActAsError] = useState("");
  const accessibilityButtonRef = useRef(null);
  const {
    organisations,
    actAsTargets,
    actingAs,
    selectedOrg,
    selectOrganisation,
    beginActAs,
  } = useOrganisation();

  async function startActAs(reason) {
    setActAsBusy(true);
    setActAsError("");
    try {
      await beginActAs(actAsTarget.id, reason);
      setActAsTarget(null);
    } catch (e) {
      setActAsError(e.message || "Could not start act-as session");
    } finally {
      setActAsBusy(false);
    }
  }

  return (
    <header
      className="navigation-focus-surface flex h-16 shrink-0 items-center justify-between border-b border-border bg-card px-6 py-3"
      role="banner"
    >
      <h1 className="text-base font-semibold text-foreground">{title}</h1>
      <div className="flex items-center gap-3">
        {/* Organisation toggle */}
        <div className="relative">
          <button
            onClick={() => setShowOrgMenu(!showOrgMenu)}
            aria-label="Switch organisation"
            aria-expanded={showOrgMenu}
            aria-haspopup="menu"
            className="flex min-h-11 items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
          >
            <Building2 className="w-4 h-4" aria-hidden="true" />
            <span className="hidden sm:inline max-w-[150px] truncate">
              {selectedOrg?.name || "Loading..."}
            </span>
            <ChevronDown className="w-3 h-3" aria-hidden="true" />
          </button>
          {showOrgMenu && organisations.length > 0 && (
            <div
              role="menu"
              className="absolute right-0 z-50 mt-1 w-56 rounded-lg border border-border bg-card shadow-lg"
            >
              <ul className="py-1">
                {actAsTargets.length > 0 && !actingAs && (
                  <li className="border-b border-white/10 pb-1 mb-1">
                    <button
                      role="menuitem"
                      onClick={() => {
                        setActAsError("");
                        setActAsTarget(actAsTargets[0]);
                        setShowOrgMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm font-semibold text-amber-700 hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
                    >
                      Act as customer…
                    </button>
                  </li>
                )}
                {organisations.map((org) => (
                  <li key={org.id}>
                    <button
                      role="menuitem"
                      onClick={() => {
                        selectOrganisation(org.id);
                        setShowOrgMenu(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FFB800] ${
                        selectedOrg?.id === org.id
                          ? "bg-[#FFB800] text-black font-semibold"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      }`}
                    >
                      {org.name}
                      {selectedOrg?.id === org.id && (
                        <span className="ml-2 text-xs opacity-70">
                          (Current)
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <button
          ref={accessibilityButtonRef}
          onClick={() => setShowA11y(true)}
          aria-label="Open accessibility settings"
          aria-haspopup="dialog"
          className="flex min-h-11 items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
        >
          <Accessibility className="w-4 h-4" aria-hidden="true" />
          <span className="hidden sm:inline">Accessibility</span>
        </button>
      </div>
      {showA11y && <AccessibilityPanel returnFocusRef={accessibilityButtonRef} onClose={() => setShowA11y(false)} />}
      <ActAsDialog
        organisation={actAsTarget}
        organisations={actAsTargets}
        onTargetChange={setActAsTarget}
        onSubmit={startActAs}
        onClose={() => setActAsTarget(null)}
        busy={actAsBusy}
        error={actAsError}
      />
    </header>
  );
}
